import { Close as CloseIcon, Search as SearchIcon, SvgIconComponent } from '@mui/icons-material';
import { Autocomplete, Box, Chip, CircularProgress, TextField } from '@mui/material';
import SearchResultItem from '../common/SearchResultItem';
import BackendService from 'BackendService';
import {
  GlobalSearchResult,
  GlobalSearchResultType,
  getGlobalSearchModelIcon,
} from 'GlobalSearchResult';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Palette } from 'Palette';

interface GlobalSearchFieldProps {
  label: string;
  placeholder?: string;
  value: GlobalSearchResult[];
  onChange: (value: GlobalSearchResult[]) => void;
  types?: GlobalSearchResultType[];
  includeRestrictedContacts?: boolean;
  includeArticles?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  tone?: keyof typeof Palette;
  // Optional custom content to render at the far left of the input,
  // before the search icon and selected chips (e.g., a type dropdown)
  leftPrefix?: React.ReactNode;
}

const GlobalSearchField: React.FC<GlobalSearchFieldProps> = ({
  label,
  placeholder = 'Search...',
  value,
  onChange,
  types,
  includeRestrictedContacts = false,
  includeArticles = false,
  multiple = true,
  disabled = false,
  tone = 'default',
  leftPrefix,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<GlobalSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const backendService = useRef(new BackendService()).current;
  const searchRef = useRef<(term: string) => Promise<void>>();

  const search = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm || searchTerm.trim() === '') {
        setOptions([]);
        return;
      }

      setLoading(true);

      try {
        const results = await backendService.globalSearch(
          searchTerm,
          includeRestrictedContacts,
          types,
          includeArticles,
        );

        console.log('Setting search results:', results);
        setOptions(results);
        console.log('setOptions called with:', results.length, 'items');
      } catch (error) {
        console.error('Global search error:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [backendService, includeRestrictedContacts, types, includeArticles],
  );

  // Store the search function in ref
  searchRef.current = search;

  useEffect(() => {
    console.log('useEffect triggered with inputValue:', inputValue);
    if (!inputValue || inputValue.trim() === '') {
      console.log('Clearing options due to empty input');
      setOptions([]);
      return;
    }

    console.log('Setting timeout for search with:', inputValue);
    const timeoutId = setTimeout(() => {
      console.log('Timeout triggered, calling search with:', inputValue);
      if (searchRef.current) {
        searchRef.current(inputValue);
      }
    }, 300);

    return () => {
      console.log('Clearing timeout');
      clearTimeout(timeoutId);
    };
  }, [inputValue]);

  const handleChange = (event: any, newValue: GlobalSearchResult | GlobalSearchResult[] | null) => {
    console.log('handleChange called with:', newValue);
    if (multiple) {
      if (Array.isArray(newValue)) {
        console.log('Setting multiple values:', newValue);
        onChange(newValue);
      } else if (newValue) {
        console.log('Setting single value as array:', [newValue]);
        onChange([newValue]);
      } else {
        console.log('Clearing values');
        onChange([]);
      }
    } else {
      if (newValue && !Array.isArray(newValue)) {
        console.log('Setting single value:', [newValue]);
        onChange([newValue]);
      } else {
        console.log('Clearing single value');
        onChange([]);
      }
    }
  };

  const handleRemove = (itemToRemove: GlobalSearchResult) => {
    const newValue = value.filter((item) => item.id !== itemToRemove.id);
    onChange(newValue);
  };

  const getOptionLabel = (option: GlobalSearchResult) => {
    return option.label;
  };

  const renderOption = (props: any, option: GlobalSearchResult) => {
    const iconConfig = getGlobalSearchModelIcon(option.type);
    const IconComponent = iconConfig ? (iconConfig.icon as SvgIconComponent) : undefined;

    return (
      <Box component="li" {...props}>
        <SearchResultItem icon={IconComponent} label={option.label} subtitle={option.subtitle} />
      </Box>
    );
  };

  const renderTags = (tagValue: GlobalSearchResult[], getTagProps: any) => {
    return tagValue.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index });
      const iconConfig = getGlobalSearchModelIcon(option.type);
      const IconComponent = iconConfig ? iconConfig.icon : null;

      return (
        <Chip
          key={key}
          {...tagProps}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {IconComponent && <IconComponent sx={{ fontSize: 16, mr: 0.5 }} />}
              {option.label}
            </Box>
          }
          onDelete={() => handleRemove(option)}
          deleteIcon={<CloseIcon />}
          size="small"
          sx={{
            backgroundColor: Palette[tone].background,
            color: Palette[tone].text,
            border: `1px solid ${Palette[tone].border}`,
            '& .MuiChip-deleteIcon': {
              color: Palette[tone].text,
              '&:hover': {
                color: Palette[tone].text,
                opacity: 0.7,
              },
            },
          }}
        />
      );
    });
  };

  console.log(
    'Current options:',
    options,
    'Loading:',
    loading,
    'InputValue:',
    inputValue,
    'Current value:',
    value,
  );

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      value={value}
      onChange={handleChange}
      forcePopupIcon={false}
      onInputChange={(event, newInputValue) => {
        console.log('Input changed to:', newInputValue);
        setInputValue(newInputValue);
      }}
      inputValue={inputValue}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      loading={loading}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={renderOption}
      renderTags={renderTags}
      filterOptions={(x) => x} // Disable local filtering since we're using server-side search
      noOptionsText={
        !inputValue || inputValue.trim() === '' ? 'Type to search' : 'No results found'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          slotProps={{
            input: {
              ...(params as any).InputProps,
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  {leftPrefix && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>{leftPrefix}</Box>
                      <Box
                        sx={{
                          width: '1px',
                          alignSelf: 'stretch',
                          backgroundColor: Palette[tone].border,
                          mr: 1,
                        }}
                      />
                    </>
                  )}
                  <SearchIcon sx={{ fontSize: 20, color: 'text.secondary', mr: 0.5 }} />
                  {(params as any).InputProps?.startAdornment}
                </Box>
              ),
              endAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {(params as any).InputProps?.endAdornment}
                </Box>
              ),
            },
            inputLabel: {
              ...(params as any).InputLabelProps,
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: Palette[tone].background,
              color: Palette[tone].text,
              '& fieldset': {
                borderColor: Palette[tone].border,
              },
              '&:hover fieldset': {
                borderColor: Palette[tone].border,
              },
              '&.Mui-focused fieldset': {
                borderColor: Palette[tone].text,
              },
            },
            '& .MuiInputLabel-root': {
              color: Palette[tone].text,
              '&.Mui-focused': {
                color: Palette[tone].text,
              },
            },
          }}
        />
      )}
    />
  );
};

export default GlobalSearchField;
